import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { useEffect } from "react";

export default function IVRPage() {
  useEffect(() => {
    const buttons = document.querySelectorAll("#accordion .acc-btn");
    buttons.forEach((btn) => {
      const icon = btn.querySelector(".acc-icon");
      btn.setAttribute("aria-expanded", "false");
      const panel = btn.nextElementSibling;
      if (panel) panel.classList.add("hidden");
      
      btn.addEventListener("click", () => {
        const panel = btn.nextElementSibling;
        const open = btn.getAttribute("aria-expanded") === "true";
        
        buttons.forEach((b) => {
          b.setAttribute("aria-expanded", "false");
          const p = b.nextElementSibling;
          if (p) p.classList.add("hidden");
          const ic = b.querySelector(".acc-icon");
          if (ic) ic.classList.remove("rotate-45");
        });
        
        btn.setAttribute("aria-expanded", String(!open));
        if (panel) panel.classList.toggle("hidden", open);
        if (icon) icon.classList.toggle("rotate-45", !open);
      });
    });
  }, []);

  useEffect(() => {
    const reveals = document.querySelectorAll<HTMLElement>('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
        } else {
          el.classList.remove('is-visible');
        }
      });
    }, { threshold: 0.15 });

    reveals.forEach((el) => observer.observe(el));
    
    reveals.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add('is-visible');
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <Navigation />
      
      {/* Section 1: What Is IVR - Dark Theme */}
{/* Section 1: What Is IVR - Dark Theme */}
<section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#040814] text-white py-20 lg:py-24 lg:px-4">
  {/* Background Effects */}
  <div className="absolute inset-0">
  </div>

  <div className="container mx-auto px-6 relative z-10">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      
      {/* LEFT CONTENT */}
      <div className="reveal space-y-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-700/30 backdrop-blur-sm">
          <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-blue-300">Interactive Voice Response</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-5xl font-bold leading-tight">
          Transform Your
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mt-2">
            Customer Experience
          </span>
        </h1>

        <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
          Interactive Voice Response (IVR) is an advanced automated telephony system that intelligently interacts with callers, 
          gathers information, and routes calls to the right destination. Transform how your business communicates with seamless voice, 
          fax, callback, and email integration.
        </p>
      </div>

      {/* RIGHT IMAGE */}
<div className="reveal">
  <div className="relative mt-8 lg:mt-0">
    {/* White background */}
    <div className="relative bg-white rounded-2xl p-4 shadow-xl border border-gray-200 w-full sm:w-[420px] mx-auto">
      <div className="rounded-2xl overflow-hidden flex justify-center">
        <img
          src="/ivr.png"
          alt="IVR System Dashboard"
          className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
        />
      </div>
    </div>
  </div>
</div>

    </div>
  </div>
</section>
      {/* Section 2: Services - Light Theme */}
      <section id="services" className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
              <span className="text-sm font-medium text-blue-700">Our Solutions</span>
            </div>
            <h2 className="reveal text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive IVR
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Services Suite
              </span>
            </h2>
            <p className="reveal text-lg text-gray-600 max-w-2xl mx-auto">
              From virtual numbers to advanced call routing, we provide everything you need for a complete IVR solution
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: "📞", title: "Virtual Numbers", desc: "Get virtual numbers with integrated IVR + call forwarding", color: "from-blue-500 to-blue-600" },
              { icon: "🧭", title: "IVR Menu", desc: "Automated voice menus with digit and extension routing", color: "from-purple-500 to-purple-600" },
              { icon: "🎵", title: "Hold Music", desc: "Professional music replacing silence during wait times", color: "from-pink-500 to-pink-600" },
              { icon: "🧩", title: "Custom Call Flow", desc: "Tailored IVR call flows for your business needs", color: "from-indigo-500 to-indigo-600" },
              { icon: "👋", title: "Greeting Messages", desc: "Custom welcome greetings for your callers", color: "from-cyan-500 to-cyan-600" },
              { icon: "🚫", title: "Blacklist", desc: "Block unwanted callers with advanced filtering", color: "from-red-500 to-red-600" },
              { icon: "📈", title: "Call Monitoring", desc: "Real-time call logs, recording, and analytics", color: "from-green-500 to-green-600" },
              { icon: "➡️", title: "Conditional Forwarding", desc: "Smart call routing based on rules and conditions", color: "from-orange-500 to-orange-600" },
              { icon: "📮", title: "Voicemail", desc: "Email-integrated voicemail for missed calls", color: "from-teal-500 to-teal-600" },
            ].map((service, idx) => (
              <div key={idx} className="reveal">
                <div className="group relative h-full bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className="absolute top-4 right-4">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${service.color} flex items-center justify-center text-white text-xl`}>
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.desc}</p>
                  
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-200 transition-all duration-300"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Usage - Dark Theme */}
      <section id="usage" className="pt-20 pb-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid.svg')] opacity-10"></div>
          <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal pl-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-700/30 backdrop-blur-sm mb-8">
                <span className="text-sm font-medium text-blue-300">Applications</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">
                Where IVR Makes
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  The Difference
                </span>
              </h2>
              
              <div className="space-y-4">
                {[
                  "Bank and stock account balances and transfers",
                  "Surveys and opinion polls",
                  "Office call routing automation",
                  "Call center intelligent forwarding",
                  "Simple order entry transactions",
                  "Selective information lookup"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 group">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-lg text-gray-300 group-hover:text-white transition-colors">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            
<div className="reveal">
  <div className="relative">
    <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 blur-2xl rounded-3xl"></div>

    <div className="relative bg-white rounded-2xl p-6 shadow-2xl w-fit mx-auto">

      <div className="rounded-xl flex justify-center">
        <img
          src="/ivr_flow.png"
          alt="IVR Flow Diagram"
          className="w-[360px] h-auto object-contain rounded-lg"
        />
      </div>

    </div>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* Section 4: Why Choose Us - Light Theme */}
<section
  id="why"
  className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
>
  <div className="container mx-auto px-6">
    <div className="text-center max-w-3xl mx-auto mb-16">
      <div className="reveal inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 mb-6">
        <span className="text-sm font-medium text-blue-700">Why Choose Us</span>
      </div>
      <h2 className="reveal text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
        Premium IVR Solutions
        <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
          Built for Excellence
        </span>
      </h2>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
      {/* LEFT SIDE - PERFECT AS IS (UNCHANGED) */}
      <div className="reveal order-2 lg:order-1">
        <div className="relative">
          <div className="absolute -inset-8 bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl blur-3xl"></div>
          <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl px-6 py-12 sm:py-24 shadow-xl border border-gray-100">
            <div className="text-center">
              <div className="w-36 h-36 sm:w-48 sm:h-48 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8 shadow-2xl">
                <span className="text-4xl sm:text-6xl text-white">IVR</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                Enterprise-Grade Features
              </h3>
              <p className="text-gray-600">
                Our IVR system combines cutting-edge technology with enterprise reliability
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {["99.9% Uptime", "256-bit Encryption", "24/7 Support", "Scalable Architecture"].map(
                (feature, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl p-4 text-center border border-gray-100"
                  >
                    <p className="font-semibold text-gray-900">{feature}</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - UPDATED CONTENT FROM IMAGES */}
{/* RIGHT SIDE - COMPACT VERSION */}
<div className="reveal order-1 lg:order-2">
  <div className="grid gap-3 sm:gap-4" id="accordion">
    {[
      {
        t: "Web, Email, CRM, Phone Connectivity",
        features: [
          "Website: can pull data from external web server",
          "CRM: can pull data from external CRM and insert data",
          "CRM: easy integration with CRM systems like SugarCRM, ZohoCRM, SalesforceCRM",
          "Email: Built-in support for email server integration",
          "Phone: Works with VOIP, PBX system with SIP extensions, and analog phone lines.",
          "Allows HTTP requests to trigger application execution",
          "Built-in support for HTTP protocol for website integration",
          "Built-in support for Java programs and libraries",
          "Built-in support for launching any external programs and getting back execution results",
        ],
      },
      {
        t: "IVR Design",
        features: [
          "Easy to use drag and drop IVR builder tool",
          "Multiple prompts in IVR menu design",
          "Multiple paths in IVR system",
          "Drag and Drop IVR GUI",
          "Unlimited questions and menu levels",
        ],
      },
      {
        t: "Security",
        features: [
          "Password protected information",
          "Connect to local database and web server",
          "On premise software means your information remains private and local",
        ],
      },
      {
        t: "Scalability",
        features: [
          "Receive multiple calls at the same time",
          "Make multiple calls at the same time",
          "Scalable up to over 1000 phone lines/channels",
          "1 call per phone line/channel",
        ],
      },
      {
        t: "More IVR Features",
        features: [
          "Calendar integration",
          "Easy to modify holidays and hours",
          "Real-time and historical reports",
        ],
      },
      {
        t: "Voice Mail and Call Queues",
        features: [
          "Call waiting queues with music on hold",
          "Supports automatic call forwarding",
          "Voice Mail to Email",
        ],
      },
      {
        t: "User Interaction",
        features: [
          "Supports touch tone/digit response",
          "Supports speech command response",
          "Built-in Automated Speech Recognition",
          "Records user information into CRM automatically",
          "Answer calls with different IVR application based on recognized caller ID info",
        ],
      },
      {
        t: "Audio and TTS",
        features: [
          "Supports Multilingual Text to Speech (Voicent plugin)",
          "Supports pre-recorded audio files",
          "Supports combination of TTS and audio files",
          "Supports audio playback and recording",
        ],
      },
      {
        t: "Inbound IVR",
        features: [
          "Call transfer to remote computer that runs Voicent RemoteAgent software",
          "Create call-in surveys",
          "Create professional business phone systems",
          "Music on hold",
          "ACD Call Routing",
          "Dial by Extension",
          "Call Center Software integration",
        ],
      },
      {
        t: "Outbound IVR",
        features: [
          "Voicent Auto Dialer software integration",
          "Voicent Appointment Reminder software integration",
          "Voicent Bulk SMS/Texting software integration",
          "Create dial out phone surveys",
          "Deliver customized notifications and reminders",
        ],
      },
    ].map((item, idx) => (
      <div key={idx} className="group">
        {/* Header */}
        <button className="acc-btn w-full text-left bg-white rounded-lg border border-gray-200 px-4 py-4 sm:px-5 sm:py-3.5 font-semibold flex items-center justify-between shadow-sm hover:shadow transition-all duration-300 text-sm sm:text-base">
          <span className="text-gray-900 text-base">{item.t}</span>
          <span className="acc-icon inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm">
            +
          </span>
        </button>

        {/* Panel */}
        <div className="acc-panel hidden px-4 sm:px-5 pb-4 pt-3 bg-gradient-to-b from-gray-50 to-white rounded-b-lg border border-gray-200 border-t-0">
          <div className="grid gap-1.5">
            {item.features.map((feature, fIdx) => (
              <div key={fIdx} className="flex items-start gap-2">
                <div className="mt-1.5 w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex-shrink-0" />
                <span className="text-[13px] text-gray-700 leading-snug">
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
</div>

    </div>
  </div>
</section>
      <Footer />
    </div>
  );
}